import { Link, useNavigate, useLocation } from "react-router-dom";
import { loginPath } from "@/lib/nextPath";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Moon, Sun, Ticket, LayoutDashboard, ShieldCheck, LogOut, User, Menu, Heart, Award, CalendarDays, GraduationCap, Store, Compass, MessagesSquare, Users, Smartphone } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSwitchRole } from "@/lib/useSwitchRole";
import { useUrgentTickets, UrgentDot } from "@/lib/urgentTickets";
import { NotificationBell } from "@/components/NotificationBell";
import { InstallAppDialog, isStandalone } from "@/components/InstallAppDialog";

export function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const urgent = useUrgentTickets(user?.role === "customer", location.pathname);
  const switchRole = useSwitchRole();
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [installOpen, setInstallOpen] = useState(false);
  useEffect(() => {
    if (user?.role !== "customer" && user?.role !== "business") { setUnreadMsgs(0); return undefined; }
    const load = () => api.get("/messages/unread").then((r) => setUnreadMsgs(r.data.unread)).catch(() => {});
    const clear = () => setUnreadMsgs(0);
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("vt-messages-read", clear);
    return () => { clearInterval(t); window.removeEventListener("vt-messages-read", clear); };
  }, [user?.role, location.pathname]);

  const dashLink =
    user?.role === "super_admin" ? "/admin" : user?.role === "business" ? "/maestro" : null;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/60">
      <nav className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2 sm:gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo"
          onClick={() => { window.scrollTo({ top: 0, behavior: "instant" }); setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 50); }}>
          <img src="/vivotaller-logo.webp" alt="VIVOTaller" className="w-9 h-9 rounded-xl object-cover shadow-sm" />
          <span className="font-display font-extrabold text-lg sm:text-xl tracking-tight">VIVOTaller</span>
        </Link>

        <div className="hidden lg:flex items-center gap-1 text-sm">
          <Link to="/explorar" data-testid="nav-explorar"
            onClick={() => setTimeout(() => document.getElementById("talleres")?.scrollIntoView({ behavior: "smooth" }), 80)}>
            <Button variant={(location.pathname === "/" || location.pathname === "/explorar") ? "secondary" : "ghost"} size="sm">Explorar</Button>
          </Link>
          {(!user || user.role === "customer") && (
            <Link to="/mis-tickets" data-testid="nav-mis-tickets">
              <Button variant="ghost" size="sm" className="relative"><Ticket className="w-4 h-4 mr-1.5" />Mis Tickets
                <UrgentDot n={urgent} testId="nav-urgent-badge" className="absolute -top-1.5 -right-1.5" />
              </Button>
            </Link>
          )}
          {user?.role === "customer" && (
            <Link to="/mis-creditos" data-testid="nav-mis-creditos">
              <Button variant={location.pathname === "/mis-creditos" ? "secondary" : "ghost"} size="sm"><Award className="w-4 h-4 mr-1.5 text-sun" />Mis créditos</Button>
            </Link>
          )}
          {user?.role === "customer" && (
            <Link to="/mis-maestros" data-testid="nav-mis-maestros">
              <Button variant={location.pathname === "/mis-maestros" ? "secondary" : "ghost"} size="sm"><Users className="w-4 h-4 mr-1.5 text-primary" />Mis Maestros</Button>
            </Link>
          )}
          {(!user || user.role === "customer") && (
            <Link to="/mis-reservas" data-testid="nav-mis-reservas">
              <Button variant="ghost" size="sm"><Heart className="w-4 h-4 mr-1.5 text-coral" />Mi Lista de Deseos</Button>
            </Link>
          )}
        </div>

        <div className="flex items-center gap-1 sm:gap-2 min-w-0">
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Cambiar tema">
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>

          {user?.role === "business" && <NotificationBell />}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="user-menu-trigger" className="relative gap-1.5 sm:gap-2 px-2 sm:px-3">
                  {unreadMsgs > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-coral text-white text-[10px] font-bold grid place-items-center" data-testid="nav-messages-badge">{unreadMsgs}</span>}
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline max-w-[120px] truncate">{user.name || user.email}</span>
                  {(user.role === "business" || user.role === "customer") && (
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${user.role === "business" ? "bg-primary text-primary-foreground" : "bg-coral/15 text-coral"}`} data-testid="profile-mode-badge">
                      {user.role === "business" ? "Modo Maestro" : "Modo Alumno"}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-xs text-muted-foreground truncate">{user.email}</div>
                <DropdownMenuSeparator />
                {user.role === "customer" && (
                  <DropdownMenuItem onClick={() => navigate("/mi-perfil")} data-testid="menu-mi-perfil">
                    <User className="w-4 h-4 mr-2" />Mi Perfil
                  </DropdownMenuItem>
                )}
                {user.role === "customer" && (
                  <DropdownMenuItem onClick={() => navigate("/mis-sesiones")} data-testid="menu-mis-sesiones">
                    <CalendarDays className="w-4 h-4 mr-2 text-primary" />Mis sesiones
                  </DropdownMenuItem>
                )}
                {dashLink && (
                  <DropdownMenuItem onClick={() => navigate(dashLink)} data-testid="menu-dashboard">
                    {user.role === "super_admin" ? <ShieldCheck className="w-4 h-4 mr-2" /> : <LayoutDashboard className="w-4 h-4 mr-2" />}
                    {user.role === "super_admin" ? "Panel Admin" : "Panel de Maestro"}
                  </DropdownMenuItem>
                )}
                {(user.role === "customer" || user.role === "business") && (
                  <DropdownMenuItem onClick={() => navigate("/mensajes")} data-testid="menu-mensajes">
                    <MessagesSquare className="w-4 h-4 mr-2 text-primary" />Mensajes
                    {unreadMsgs > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-coral text-white text-[10px] font-bold grid place-items-center">{unreadMsgs}</span>}
                  </DropdownMenuItem>
                )}
                {user.role === "customer" && (
                  <DropdownMenuItem onClick={() => navigate("/mis-tickets")} data-testid="menu-mis-tickets">
                    <Ticket className="w-4 h-4 mr-2" />Mis Tickets
                  </DropdownMenuItem>
                )}
                {user.role === "customer" && (
                  <DropdownMenuItem onClick={() => navigate("/mis-creditos")} data-testid="menu-mis-creditos">
                    <Award className="w-4 h-4 mr-2 text-sun" />Mis créditos
                  </DropdownMenuItem>
                )}
                {user.role === "customer" && (
                  <DropdownMenuItem onClick={() => navigate("/mis-reservas")} data-testid="menu-mis-reservas">
                    <Heart className="w-4 h-4 mr-2 text-coral" />Mi Lista de Deseos
                  </DropdownMenuItem>
                )}
                {user.can_switch_role && (
                  <DropdownMenuItem onClick={() => switchRole(user.role === "business" ? "customer" : "business")} data-testid="menu-switch-role">
                    {user.role === "business"
                      ? <><GraduationCap className="w-4 h-4 mr-2 text-primary" />Mi perfil alumno</>
                      : <><Store className="w-4 h-4 mr-2 text-primary" />Perfil Maestro</>}
                  </DropdownMenuItem>
                )}
                {user.role === "business" && (
                  <DropdownMenuItem onClick={() => navigate("/explorar")} className="sm:hidden" data-testid="menu-explorar">
                    <Compass className="w-4 h-4 mr-2 text-primary" />Explorar Talleres
                  </DropdownMenuItem>
                )}
                {(user.role === "business" || user.role === "customer") && !isStandalone() && (
                  <DropdownMenuItem onClick={() => setInstallOpen(true)} data-testid="menu-install-app">
                    <Smartphone className="w-4 h-4 mr-2 text-primary" />Instalar App
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { navigate("/"); await logout(); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to={loginPath(location.pathname)} data-testid="nav-login">
                <Button variant="ghost" size="sm">Ingresar</Button>
              </Link>
              <Link to="/registro?role=business" data-testid="nav-publicar" className="hidden sm:block">
                <Button size="sm" className="font-medium">Publicar mi Taller</Button>
              </Link>
            </>
          )}
        </div>
      </nav>
      <InstallAppDialog open={installOpen} onOpenChange={setInstallOpen} role={user?.role} />
    </header>
  );
}
